import axios from "axios";
import { Helmet } from "react-helmet";
import React, { useState, useEffect, useContext } from "react";

import {
  BiWalletAlt,
  BiUserPlus,
  BiChevronDown,
  BiChevronUp,
  BiCalendar,
} from "react-icons/bi";
import { Button, Collapse, Table, Card, Form, Row, Col } from "react-bootstrap";

import styles from "./refer.module.css";
import Referredusers from "../../components/ReferAndEarn/ReferredUsers";
import Statcard from "../../components/ReferAndEarn/StatCard";
import Referralitem from "../../components/ReferAndEarn/ReferralItem";
import Globalcontext from "../../context/GlobalContext";

const API_URL = process.env.REACT_APP_API_URL;

const ReferAndEarn = () => {
  const [testSeriesCount, setTestSeriesCount] = useState(0);
  const [courseCount, setCourseCount] = useState(0);
  const [comboCount, setComboCount] = useState(0);
  const { userProfile } = useContext(Globalcontext);
  const [userReferral, setUserReferral] = useState(null);
  const [showItems, setShowItems] = useState(false);
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);

  // Daily breakdown states
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loadingDaily, setLoadingDaily] = useState(false);

  // Helper function to get month name
  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1];
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(thirtyDaysAgo.toISOString().split("T")[0]);
  }, []);

  // Handle month click to show daily breakdown
  const handleMonthClick = async (year, month) => {
    // Set date range for the clicked month
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0);

    setStartDate(startOfMonth.toISOString().split("T")[0]);
    setEndDate(endOfMonth.toISOString().split("T")[0]);

    // Fetch daily breakdown
    setLoadingDaily(true);
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${API_URL}/v1/users/referral/daily?startDate=${
          startOfMonth.toISOString().split("T")[0]
        }&endDate=${endOfMonth.toISOString().split("T")[0]}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setDailyData(response.data.data);

        // Scroll to daily breakdown section
        setTimeout(() => {
          const dailySection = document.getElementById(
            "daily-breakdown-section"
          );
          if (dailySection) {
            dailySection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Unable to fetch daily referral data", error);
      alert("Failed to fetch daily breakdown. Please try again.");
    } finally {
      setLoadingDaily(false);
    }
  };

  useEffect(() => {
    fetchUserReferral();
  }, []);

  useEffect(() => {
    let testCnt = 0;
    let courseCnt = 0;
    let comboCnt = 0;
    userReferral?.referralItems?.forEach((item) => {
      if (item?.itemId?.itemType === "testSeries") {
        testCnt += 1;
      } else if (item?.itemId?.itemType === "course") {
        courseCnt += 1;
      } else {
        comboCnt += 1;
      }
    });
    setTestSeriesCount(testCnt);
    setCourseCount(courseCnt);
    setComboCount(comboCnt);
  }, [userReferral]);

  const fetchUserReferral = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${API_URL}/v1/users/referral`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(
        (response) => {
          if (response.status === 200) {
            setUserReferral(response.data.data);
          }
        },
        (error) => {
          console.log("Unable to fetch user referral");
          console.log(error);
        }
      )
      .catch((error) => {
        console.log("Unable to fetch user referral");
        console.log(error);
      });
  };

  const fetchDailyBreakdown = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date cannot be greater than end date");
      return;
    }

    setLoadingDaily(true);
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${API_URL}/v1/users/referral/daily?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setDailyData(response.data.data);
      }
    } catch (error) {
      console.error("Unable to fetch daily referral data", error);
      alert("Failed to fetch daily breakdown. Please try again.");
    } finally {
      setLoadingDaily(false);
    }
  };

  // Calculate total earnings for the selected date range
  const calculateTotalEarnings = () => {
    if (!dailyData?.dailyEarnings) return 0;
    return dailyData.dailyEarnings.reduce(
      (total, day) => total + day.totalEarnings,
      0
    );
  };

  // Flatten daily data for table display
  const getTableRows = () => {
    if (!dailyData?.dailyEarnings) return [];

    const rows = [];
    dailyData.dailyEarnings.forEach((day) => {
      day.items.forEach((item) => {
        rows.push({
          date: day.date,
          itemTitle: item.itemTitle,
          earningAmount: item.earningAmount,
        });
      });
    });

    // Sort by date in ascending order
    return rows.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  return (
    <div>
      <Helmet>
        <title>Refer and Earn Program | MathonGo</title>
        <meta name="description" content="Refer & Earn program" />
        <link rel="icon" href="/favicon.ico" />
      </Helmet>
      <div className="container">
        <div
          className="row"
          style={{
            borderRadius: "12px",
            boxShadow: "0px 4px 24px 0px #00000014",
          }}
        >
          <div className="col-12 col-md-8">
            <div className="card-body p-4">
              <h4 className={`${styles.header2} mb-5`}>
                Invite{" "}
                {userProfile && userProfile.isInfluencer
                  ? "Students"
                  : "your friends"}{" "}
                and get <span className="text-danger">cashback</span>
              </h4>
              <div className="row mt-7">
                <div className="col-6 col-md-4 text-center">
                  <img
                    className="mb-3"
                    src="/img/Group 1.svg"
                    alt="step 1"
                    height="50px"
                    width="50px"
                  />
                  <h5 className={`${styles.refer_header}`}>
                    Refer your friends
                  </h5>
                  <p className="text-muted">Just share your referral link</p>
                </div>
                <div className="col-6 col-md-4 text-center">
                  <img
                    className="mb-3"
                    src="/img/Group 2.svg"
                    alt="step 1"
                    height="50px"
                    width="50px"
                  />
                  <h5 className={`${styles.refer_header}`}>
                    Your friends join us
                  </h5>
                  <p className="text-muted">They receive some amount off</p>
                </div>
                <div className="col-6 col-md-4 text-center">
                  <img
                    className="mb-3"
                    src="/img/Group 3.svg"
                    alt="step 1"
                    height="50px"
                    width="50px"
                  />
                  <h5 className={`${styles.refer_header}`}>
                    You make savings!
                  </h5>
                  <p className="text-muted">
                    You make some amount, easily withdraw it!
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            className="col-4 d-none d-lg-block"
            style={{
              backgroundImage:
                'url("/img/undraw_social_friends_re_7uaa%201.svg")',
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }}
          ></div>
        </div>
        <div className="row mt-7 mb-4">
          <div className="col-12 px-0">
            <div className="d-flex">
              <div className={`${styles.rounded_icon} shadow`}>
                <img
                  className={`${styles.inner_icon}`}
                  src="/img/Shape.svg"
                  height="16px"
                  width="16px"
                  alt="note icon"
                />
              </div>
              <h4 className={`${styles.header2} mt-3 ml-2`}>
                Your Referral Overview
              </h4>
            </div>
          </div>
        </div>
        {/* Desktop Layout */}
        <div className="row d-none d-md-flex">
          <div className="col-lg-4 col-md-6">
            <div>
              <p className="font-weight-bold mb-1">Number of Users Referred</p>
              <p className={`${styles.number_stat}`}>
                {userReferral?.referredUsers?.length}
              </p>
            </div>
            <div className="mt-8">
              <p className="font-weight-bold mb-1">Detailed Report</p>
              <p className={`${styles.number_stat}`}>
                <Button variant="success" size="xs" href="/wallet/">
                  <BiWalletAlt className="mb-1" /> Open Wallet
                </Button>
              </p>
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <p className="font-weight-bold mb-1">Total Earnings</p>
            <p className={`${styles.number_stat}`}>
              ₹
              {userReferral?.referredUsers
                ?.reduce((a, v) => (a = a + v.earningAmount), 0)
                ?.toLocaleString("en-IN") || 0}
            </p>
          </div>
          {userReferral?.monthlyEarnings &&
            userReferral.monthlyEarnings.length > 0 && (
              <div className="col-lg-4 col-md-6">
                <p className="font-weight-bold mb-1">Monthly Breakdown</p>
                <div
                  style={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    paddingRight: "15px",
                  }}
                >
                  {userReferral.monthlyEarnings
                    .sort((a, b) => {
                      if (a.year === b.year) {
                        return b.month - a.month;
                      }
                      return b.year - a.year;
                    })
                    .map((monthData, index) => (
                      <div
                        key={`${monthData.year}-${monthData.month}`}
                        className="mb-2 d-flex justify-content-between align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          handleMonthClick(monthData.year, monthData.month)
                        }
                        title="Click to view daily breakdown for this month"
                      >
                        <span
                          className="text-primary"
                          style={{ fontSize: "0.9rem" }}
                        >
                          {getMonthName(monthData.month)} {monthData.year}
                        </span>
                        <span
                          className="font-weight-bold"
                          style={{ fontSize: "0.9rem" }}
                        >
                          ₹
                          {monthData.totalEarnings?.toLocaleString("en-IN") ||
                            0}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
        </div>

        {/* Mobile Layout */}
        <div className="d-md-none">
          {/* First Row - Number of Users and Total Earnings */}
          <div className="row">
            <div className="col-6">
              <div>
                <p className="font-weight-bold mb-1">
                  Number of Users Referred
                </p>
                <p className={`${styles.number_stat}`}>
                  {userReferral?.referredUsers?.length}
                </p>
              </div>
            </div>
            <div className="col-6">
              <p className="font-weight-bold mb-1">Total Earnings</p>
              <p className={`${styles.number_stat}`}>
                ₹
                {userReferral?.referredUsers
                  ?.reduce((a, v) => (a = a + v.earningAmount), 0)
                  ?.toLocaleString("en-IN") || 0}
              </p>
            </div>
          </div>

          {/* Second Row - Monthly Breakdown (if available) */}
          {userReferral?.monthlyEarnings &&
            userReferral.monthlyEarnings.length > 0 && (
              <div className="row mt-4">
                <div className="col-12">
                  <p className="font-weight-bold mb-1">Monthly Breakdown</p>
                  <div
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      paddingRight: "15px",
                    }}
                  >
                    {userReferral.monthlyEarnings
                      .sort((a, b) => {
                        if (a.year === b.year) {
                          return b.month - a.month;
                        }
                        return b.year - a.year;
                      })
                      .map((monthData, index) => (
                        <div
                          key={`${monthData.year}-${monthData.month}`}
                          className="mb-2 d-flex justify-content-between align-items-center"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            handleMonthClick(monthData.year, monthData.month)
                          }
                          title="Click to view daily breakdown for this month"
                        >
                          <span
                            className="text-primary"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {getMonthName(monthData.month)} {monthData.year}
                          </span>
                          <span
                            className="font-weight-bold"
                            style={{ fontSize: "0.9rem" }}
                          >
                            ₹
                            {monthData.totalEarnings?.toLocaleString("en-IN") ||
                              0}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

          {/* Third Row - Detailed Report */}
          <div className="row mt-4">
            <div className="col-12">
              <p className="font-weight-bold mb-1">Detailed Report</p>
              <p className={`${styles.number_stat}`}>
                <Button variant="success" size="xs" href="/wallet/">
                  <BiWalletAlt className="mb-1" /> Open Wallet
                </Button>
              </p>
            </div>
          </div>
        </div>

        {/* Daily Breakdown Section */}
        <div className="row mt-7 mb-4" id="daily-breakdown-section">
          <div className="col-12 px-0">
            <div className="d-flex">
              <div className={`${styles.rounded_icon} shadow`}>
                <BiCalendar
                  className={`${styles.inner_icon}`}
                  style={{ fontSize: "16px", color: "#335eea" }}
                />
              </div>
              <h4 className={`${styles.header2} mt-3 ml-2`}>
                Daily Earnings Breakdown
              </h4>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <Card className="shadow-sm">
              <Card.Body>
                <Row className="align-items-end">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ visibility: "hidden" }}>
                        Button
                      </Form.Label>
                      <Button
                        variant="primary"
                        onClick={fetchDailyBreakdown}
                        disabled={loadingDaily}
                        className="w-100"
                      >
                        {loadingDaily ? "Loading..." : "Get Daily Breakdown"}
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Total Earnings Card and Daily Breakdown Table */}
        {dailyData && (
          <>
            {/* Mobile Layout - Stacked */}
            <div className="d-md-none">
              {/* Total Earnings Card - Full Width on Mobile */}
              <div className="row mb-4">
                <div className="col-12">
                  <Card className="bg-success text-white">
                    <Card.Body className="text-center">
                      <h5 className="mb-3">Total Earnings</h5>
                      <h3 className="mb-3">
                        ₹{calculateTotalEarnings().toLocaleString("en-IN")}
                      </h3>
                      <small>
                        From {formatDate(dailyData.dateRange.startDate)} to{" "}
                        {formatDate(dailyData.dateRange.endDate)}
                      </small>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              {/* Daily Breakdown Table - Full Width on Mobile */}
              <div className="row mb-4">
                <div className="col-12">
                  <Card className="shadow-sm">
                    <Card.Header>
                      <h5 className="mb-0">Daily Earnings Details</h5>
                    </Card.Header>
                    <Card.Body style={{ padding: 0 }}>
                      {getTableRows().length > 0 ? (
                        <div
                          style={{
                            height: "400px",
                            overflowY: "auto",
                            overflowX: "auto",
                          }}
                        >
                          <Table
                            striped
                            bordered
                            hover
                            responsive
                            className="mb-0"
                          >
                            <thead
                              className="bg-primary text-white"
                              style={{ position: "sticky", top: 0, zIndex: 10 }}
                            >
                              <tr>
                                <th
                                  className="d-none d-sm-table-cell"
                                  style={{
                                    minWidth: "60px",
                                    textAlign: "center",
                                  }}
                                >
                                  S.No
                                </th>
                                <th
                                  style={{
                                    minWidth: "100px",
                                    textAlign: "center",
                                  }}
                                >
                                  Date
                                </th>
                                <th
                                  style={{
                                    minWidth: "180px",
                                    textAlign: "center",
                                  }}
                                >
                                  Item
                                </th>
                                <th
                                  style={{
                                    minWidth: "90px",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Amount Earned
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {getTableRows().map((row, index) => (
                                <tr key={index}>
                                  <td
                                    className="d-none d-sm-table-cell"
                                    style={{ textAlign: "center" }}
                                  >
                                    {index + 1}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {formatDate(row.date)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "left",
                                      paddingLeft: "12px",
                                    }}
                                  >
                                    {row.itemTitle}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    ₹{row.earningAmount.toLocaleString("en-IN")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <p className="text-muted mb-0">
                            No earnings found for the selected date range.
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>

            {/* Desktop Layout - Side by Side */}
            <div className="d-none d-md-block">
              <div className="row mb-4">
                {/* Total Earnings Card */}
                <div className="col-md-4">
                  <Card className="bg-success text-white h-100">
                    <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center h-100">
                      <div>
                        <h5 className="mb-3">Total Earnings</h5>
                        <h3 className="mb-3">
                          ₹{calculateTotalEarnings().toLocaleString("en-IN")}
                        </h3>
                        <small>
                          From {formatDate(dailyData.dateRange.startDate)} to{" "}
                          {formatDate(dailyData.dateRange.endDate)}
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>

                {/* Daily Breakdown Table */}
                <div className="col-md-8">
                  <Card className="shadow-sm h-100">
                    <Card.Header>
                      <h5 className="mb-0">Daily Earnings Details</h5>
                    </Card.Header>
                    <Card.Body style={{ padding: 0 }}>
                      {getTableRows().length > 0 ? (
                        <div
                          style={{
                            height: "400px",
                            overflowY: "auto",
                            overflowX: "auto",
                          }}
                        >
                          <Table
                            striped
                            bordered
                            hover
                            responsive
                            className="mb-0"
                          >
                            <thead
                              className="bg-primary text-white"
                              style={{ position: "sticky", top: 0, zIndex: 10 }}
                            >
                              <tr>
                                <th
                                  style={{
                                    minWidth: "60px",
                                    textAlign: "center",
                                  }}
                                >
                                  S.No
                                </th>
                                <th
                                  style={{
                                    minWidth: "120px",
                                    textAlign: "center",
                                  }}
                                >
                                  Date
                                </th>
                                <th
                                  style={{
                                    minWidth: "250px",
                                    textAlign: "center",
                                  }}
                                >
                                  Item
                                </th>
                                <th
                                  style={{
                                    minWidth: "100px",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Amount Earned
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {getTableRows().map((row, index) => (
                                <tr key={index}>
                                  <td style={{ textAlign: "center" }}>
                                    {index + 1}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {formatDate(row.date)}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "left",
                                      paddingLeft: "12px",
                                    }}
                                  >
                                    {row.itemTitle}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    ₹{row.earningAmount.toLocaleString("en-IN")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <p className="text-muted mb-0">
                            No earnings found for the selected date range.
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>
          </>
        )}

        {/*  */}
        <div className="row mt-7 mb-4">
          <div className="col-12 px-0">
            <div className="d-flex">
              <div className={`${styles.rounded_icon} shadow`}>
                <img
                  className={`${styles.inner_icon}`}
                  src="/img/Shape.svg"
                  height="16px"
                  width="16px"
                  alt="note icon"
                />
              </div>
              <h4 className={`${styles.header2} mt-3 ml-2`}>
                Your Referral Links
              </h4>
            </div>
          </div>
        </div>
        {/* Course */}
        {courseCount > 0 && (
          <>
            <div className="row">
              <h4 className={`${styles.header2} mt-3 ml-2`}>Courses:</h4>
            </div>
            <div className="row">
              {userReferral?.referralItems?.map((item) => {
                if (item?.itemId?.itemType === "course") {
                  return (
                    <div className="col-12 col-sm-6 col-md-4">
                      <Referralitem
                        item={item}
                        studentCode={userReferral?.studentCode}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            </div>
          </>
        )}
        {/* Test series */}
        {testSeriesCount > 0 && (
          <>
            <div className="row">
              <h4 className={`${styles.header2} mt-3 ml-2`}>Test Series:</h4>
            </div>
            <div className="row">
              {userReferral?.referralItems?.map((item) => {
                if (item?.itemId?.itemType === "testSeries") {
                  return (
                    <div className="col-12 col-sm-6 col-md-4">
                      <Referralitem
                        item={item}
                        studentCode={userReferral?.studentCode}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            </div>
          </>
        )}

        {/* Combo Courses */}
        {comboCount > 0 && (
          <>
            <div className="row">
              <h4 className={`${styles.header2} mt-3 ml-2`}>Courses:</h4>
            </div>
            <div className="row">
              {userReferral?.referralItems?.map((item) => {
                if (item?.itemId?.itemType === "courseTestSeriesCombo") {
                  return (
                    <div className="col-12 col-sm-6 col-md-4">
                      <Referralitem
                        item={item}
                        studentCode={userReferral?.studentCode}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferAndEarn;
